import React, { useState, useRef, useEffect } from "react";
import "./otpInput.scss";

type OTPInputProps = {
  length: number;
  onChange: (otp: string) => void;
};

const OTPInput: React.FC<OTPInputProps> = ({ length, onChange }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  
  // Store a ref for each input in an array
  const inputRefs = useRef<HTMLInputElement[]>([]);

  // Focus the first input on mount
  useEffect(() => {
    // If there is at least one input, focus it
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return; // ignore non-numeric input

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    onChange(newOtp.join(""));

    // Move focus to next input if available
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = pasteData
      .split("")
      .filter((char) => !isNaN(Number(char)) && char.trim() !== "");

    if (digits.length === 0) return;

    const newOtp = [...otp];
    for (let i = 0; i < digits.length && index + i < length; i++) {
      newOtp[index + i] = digits[i];
    }
    setOtp(newOtp);
    onChange(newOtp.join(""));

    const focusIndex = Math.min(index + digits.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="otp-input-container">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          type="text"
          maxLength={1}
          value={otp[i]}
          onChange={(e) => handleChange(e.target.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          className="otp-input"
          // Assign each input to the array of refs
          ref={(el) => {
            if (el) {
              inputRefs.current[i] = el;
            }
          }}
        />
      ))}
    </div>
  );
};

export default OTPInput;
